import styled, { css } from "styled-components";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { ThemeType } from "../../../theme/Theme";

export const CardsGrid = styled.div<{ $equalRows?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(22rem, 1fr));
  grid-auto-rows: ${({ $equalRows }) => ($equalRows ? "1fr" : "auto")};
  gap: ${Spacing.l};
`;

export const Card = styled.div<{ $accent?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border: ${({ theme, $accent }) =>
    $accent ? `2px solid ${theme.main}` : `1px solid ${theme.darkSub}`};
  border-radius: ${Rounded.xl};
  overflow: hidden;
  background-color: ${({ theme }) => theme.white};
`;

export const CardHeader = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  background-color: ${({ theme }) => theme.lightSub};
  border-block-end: 1px solid ${({ theme }) => theme.darkSub};
`;

// The two opaque gradients ride with the content and hide the fixed shadows at
// each end, so a shadow only shows on the side that has more to scroll to.
export const scrollShadows = css`
  background:
    linear-gradient(${({ theme }) => theme.white} 30%, transparent) center top,
    linear-gradient(transparent, ${({ theme }) => theme.white} 70%) center
      bottom,
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.16), transparent)
      center top,
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.16), transparent)
      center bottom;
  background-repeat: no-repeat;
  background-size:
    100% 2rem,
    100% 2rem,
    100% 0.75rem,
    100% 0.75rem;
  background-attachment: local, local, scroll, scroll;
`;

export const CardBody = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  min-height: 0;
  overflow-y: auto;
  ${scrollShadows}

  & > * {
    flex-shrink: 0;
  }
`;

export const CardFooter = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border-block-start: 1px solid ${({ theme }) => theme.darkSub};
`;

export const ActionButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs} ${Spacing.s};
  border: 1px solid ${({ theme }) => theme.main};
  background-color: ${({ theme }) => theme.white} !important;
  color: ${({ theme }) => theme.main} !important;

  &:hover {
    background-color: ${({ theme }) => theme.sub} !important;
  }
`;

export const GhostButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs} ${Spacing.s};
  border: 1px solid ${({ theme }) => theme.darkSub};
  background-color: ${({ theme }) => theme.white} !important;
  color: ${({ theme }) => theme.textColor} !important;

  &:hover {
    background-color: ${({ theme }) => theme.sub} !important;
  }
`;

export const ClampedText = styled(Text)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

export const TooltipHost = styled(Flex)`
  button:disabled {
    pointer-events: none;
  }
`;

export const Badge = styled.div<{ $tone: keyof ThemeType }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding-inline: ${Spacing.s};
  padding-block: ${Spacing.xs};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme, $tone }) => theme[$tone]};
  width: fit-content;
  flex-shrink: 0;
  overflow: hidden;
`;
